import { AsyncReportType } from '@app-core/constants/constants';

export const TOP_DRIVERS_TABLE_COLUMNS = ['driver', 'eventsPer100Units', 'distance'];

export const REQUIRE_COACHING_TABLE_COLUMNS = ['driver', 'eventsPer100Units', 'distance', 'riskFactor'];

export const MOST_IMPROVED_TABLE_COLUMNS = ['driver', 'eventsPer100Units', 'distance', 'percentageChange'];

export const REPORT_LIST = [
  {
    key: 'all',
    label: 'All Reports',
  },
  {
    key: 'fleet-safety-report',
    label: 'Fleet Safety Report',
  },
  {
    key: 'coaching-effectiveness-report',
    label: 'Coaching Effectiveness Report',
  },
];

export const EXPORT_HISTORY_TABLE_COLUMN: string[] = [
  'requestType',
  'requestedTime',
  'requestedBy',
  'status',
  'fileSize',
  'fileFormat',
  'action',
];

export const SCHEDULED_REPORTS_LIST_COLUMN: string[] = [
  'scheduleName',
  'reportType',
  'reportDuration',
  'frequency',
  'owner',
  'lastModified',
  'status',
  'additionalRecipients',
  'action',
];

export enum DownloadStatus {
  Uploaded = 'UPLOADED',
  Failed = 'FAILED',
  Pending = 'PENDING',
  NoData = 'NO_DATA',
}

export const ReportType = AsyncReportType;
export const DEFAULT_THEME = 'light';

export const DOWNLOADS_DURATION_LIST = [
  {
    title: 'Past 30 days',
    days: 30,
  },
  {
    title: 'Past 60 days',
    days: 30 * 2,
  },
  {
    title: 'Past 90 days',
    days: 30 * 3,
  },
  {
    title: 'Past 180 days',
    days: 30 * 6,
  },
];

export const REPORTS_DURATION_LIST = {
  everyDay: {
    label: 'Every Day',
    key: 1,
  },
  threeDays: {
    label: 'Past 3 days',
    key: 3,
  },
  sevenDays: {
    label: 'Past 7 days',
    key: 7,
  },
  fourteenDays: {
    label: 'Past 14 days',
    key: 14,
  },
  thirtyDays: {
    label: 'Past 30 days',
    key: 30,
  },
  sixtyDays: {
    label: 'Past 60 days',
    key: 30 * 2,
  },
  ninetyDays: {
    label: 'Past 90 days',
    key: 30 * 3,
  },
};

export const FREQUENCY = {
  IMMEDIATE: {
    label: 'Immediate',
    key: 'IMMEDIATE',
  },
  DAILY: {
    label: 'Daily',
    key: 'DAILY',
  },
  WEEKLY: {
    label: 'Weekly',
    key: 'WEEKLY',
  },
  MONTHLY: {
    label: 'Monthly',
    key: 'MONTHLY',
  },
};

export const FREQUENCY_WEEKLY = [
  {
    label: 'Every Monday',
    key: 1,
  },
  {
    label: 'Every Tuesday',
    key: 2,
  },
  {
    label: 'Every Wednesday',
    key: 3,
  },
  {
    label: 'Every Thursday',
    key: 4,
  },
  {
    label: 'Every Friday',
    key: 5,
  },
  {
    label: 'Every Saturday',
    key: 6,
  },
  {
    label: 'Every Sunday',
    key: 0,
  },
];

export const FREQUENCY_MONTHLY = [
  { label: 'First day of the month', key: 1 },
  { label: 'Last day of the month', key: 31 },
  ...Array.from({ length: 29 }, (_, index) => index + 2),
];

export const TIME = Array.from({ length: 48 }, (_, i) => {
  const hours = String(Math.floor(i / 2)).padStart(2, '0');
  const minutes = i % 2 === 0 ? '00' : '30';
  return `${hours}:${minutes}`;
});

export const COACHING_REPORT_TYPES = {
  fleetSafety: {
    key: 'FLEET_SAFETY',
    label: 'Fleet Safety Report',
    subtypes: [],
    supportsTagsFilter: true,
    supportsAssetDriverFilter: false,
    reportDuration: [REPORTS_DURATION_LIST.thirtyDays, REPORTS_DURATION_LIST.sixtyDays, REPORTS_DURATION_LIST.ninetyDays],
    frequency: [FREQUENCY.MONTHLY],
    additionalParams: ['unit', 'language', 'includeInsignificantPoints'],
  },
  coachingSession: {
    key: 'COACHING_SESSION',
    label: 'Coaching Session Report',
    subtypes: [],
    supportsTagsFilter: false,
    supportsAssetDriverFilter: false,
    reportDuration: [],
    frequency: [FREQUENCY.IMMEDIATE],
    additionalParams: ['unit', 'language', 'dateFormat'],
    featureFlag: 'reports:coaching_session_report',
  },
  privacyModeEventsReport: {
    key: 'PRIVACY_MODE',
    label: 'Privacy Mode Report',
    subtypes: [],
    supportsTagsFilter: true,
    supportsAssetDriverFilter: false,
    reportDuration: [
      REPORTS_DURATION_LIST.threeDays,
      REPORTS_DURATION_LIST.sevenDays,
      REPORTS_DURATION_LIST.fourteenDays,
      REPORTS_DURATION_LIST.thirtyDays,
    ],
    frequency: [FREQUENCY.DAILY, FREQUENCY.WEEKLY, FREQUENCY.MONTHLY],
  },
  eventListReport: {
    key: 'EVENT_LIST',
    label: 'Event List Report',
    subtypes: [],
    supportsTagsFilter: true,
    supportsAssetDriverFilter: true,
    reportDuration: [
      REPORTS_DURATION_LIST.everyDay,
      REPORTS_DURATION_LIST.threeDays,
      REPORTS_DURATION_LIST.sevenDays,
      REPORTS_DURATION_LIST.fourteenDays,
      REPORTS_DURATION_LIST.thirtyDays,
    ],
    frequency: [FREQUENCY.DAILY, FREQUENCY.WEEKLY, FREQUENCY.MONTHLY],
    supportsEventTypeFilter: true,
  },
  eventCountReport: {
    key: 'EVENT_COUNT',
    label: 'Event Count Report',
    subtypes: [],
    supportsTagsFilter: true,
    supportsAssetDriverFilter: true,
    reportDuration: [
      REPORTS_DURATION_LIST.threeDays,
      REPORTS_DURATION_LIST.sevenDays,
      REPORTS_DURATION_LIST.fourteenDays,
      REPORTS_DURATION_LIST.thirtyDays,
    ],
    frequency: [FREQUENCY.DAILY, FREQUENCY.WEEKLY, FREQUENCY.MONTHLY],
  },
  highGeventReport: {
    key: 'POTENTIAL_CRASH_EVENTS',
    label: 'High-G Event List Report',
    subtypes: [],
    supportsTagsFilter: true,
    supportsAssetDriverFilter: false,
    reportDuration: [
      REPORTS_DURATION_LIST.threeDays,
      REPORTS_DURATION_LIST.sevenDays,
      REPORTS_DURATION_LIST.fourteenDays,
      REPORTS_DURATION_LIST.thirtyDays,
    ],
    frequency: [FREQUENCY.DAILY, FREQUENCY.WEEKLY, FREQUENCY.MONTHLY],
  },
  customEventReport: {
    key: 'CUSTOM_REPORT',
    label: 'Custom Event List Report',
    subtypes: [],
    supportsTagsFilter: true,
    supportsAssetDriverFilter: false,
    reportDuration: [
      REPORTS_DURATION_LIST.threeDays,
      REPORTS_DURATION_LIST.sevenDays,
      REPORTS_DURATION_LIST.fourteenDays,
      REPORTS_DURATION_LIST.thirtyDays,
    ],
    frequency: [FREQUENCY.DAILY, FREQUENCY.WEEKLY, FREQUENCY.MONTHLY],
  },
  videorequestList: {
    key: 'FLEET_DVR_REQUESTS',
    label: 'Video Request List Report',
    subtypes: [],
    supportsTagsFilter: true,
    supportsAssetDriverFilter: true,
    reportDuration: [
      REPORTS_DURATION_LIST.threeDays,
      REPORTS_DURATION_LIST.sevenDays,
      REPORTS_DURATION_LIST.fourteenDays,
      REPORTS_DURATION_LIST.thirtyDays,
    ],
    frequency: [FREQUENCY.DAILY, FREQUENCY.WEEKLY, FREQUENCY.MONTHLY],
  },
  panicButton: {
    key: 'PANIC_EVENTS',
    label: 'Panic Button List Report',
    subtypes: [],
    supportsTagsFilter: true,
    supportsAssetDriverFilter: false,
    reportDuration: [
      REPORTS_DURATION_LIST.threeDays,
      REPORTS_DURATION_LIST.sevenDays,
      REPORTS_DURATION_LIST.fourteenDays,
      REPORTS_DURATION_LIST.thirtyDays,
    ],
    frequency: [FREQUENCY.DAILY, FREQUENCY.WEEKLY, FREQUENCY.MONTHLY],
  },
};

const { ninetyDays, ...remainingDurations } = REPORTS_DURATION_LIST;

const { sixtyDays, ...durationForTrips } = remainingDurations;

export const DIAGNOSTICS_REPORT_TYPES = {
  fleetHealthEvents: {
    key: 'FLEET_HEALTH_EVENTS',
    label: 'Device Diagnostics Events',
    subtypes: [
      { key: 'ALL', label: 'All (Consolidated)' },
      { key: 'PowerCutAlert', label: 'Power Cut Alert' },
      { key: 'DeviceOffline', label: 'Device Offline' },
      { key: 'UnprovisionedDeviceDetected', label: 'Incorrectly Provisioned Devices' },
      { key: 'SDCardNeedFormatting', label: 'SD Card Needs Formatting' },
      { key: 'ExternalStorageAvailable', label: 'SD Card Unavailable' },
    ],
    supportsTagsFilter: false,
    supportsAssetDriverFilter: false,
    reportDuration: Object.values(remainingDurations),
    frequency: [FREQUENCY.DAILY, FREQUENCY.WEEKLY],
    featureFlag: 'diagnostics_tab',
  },

  fleetCameraEvents: {
    key: 'FLEET_CAMERA_EVENTS',
    label: 'Camera Visibility Events',
    subtypes: [
      { key: 'ALL', label: 'All (Consolidated)' },
      { key: 'BadMountingRoadCamera', label: 'Incorrect Road Camera Mounting' },
      { key: 'BadMountingDriverCamera', label: 'Incorrect Driver Camera Mounting' },
      { key: 'RoadCameraLensCovered', label: 'Road Camera Lens Obstructed' },
      { key: 'DriverCameraLensCovered', label: 'Driver Camera Lens Obstructed' },
      { key: 'BadMountingInertialSensor', label: 'Incorrect Camera Position' },
    ],
    supportsTagsFilter: false,
    supportsAssetDriverFilter: false,
    reportDuration: Object.values(remainingDurations),
    frequency: [FREQUENCY.DAILY, FREQUENCY.WEEKLY],
    featureFlag: 'diagnostics_tab',
  },

  deviceOnlineStatus: {
    key: 'FLEET_DEVICE_PING',
    label: 'Device Online Status',
    subtypes: [
      { key: 'ALL', label: 'All (Consolidated)' },
      { key: 'zeroDays', label: 'Inactive Days(0)' },
      { key: 'oneToFiveDays', label: 'Inactive Days(1 to 5)' },
      { key: 'sixToTenDays', label: 'Inactive Days(6 to 10)' },
      { key: 'moreThanTenDays', label: 'Inactive Days(10 or more)' },
    ],
    supportsTagsFilter: false,
    supportsAssetDriverFilter: false,
    reportDuration: Object.values(remainingDurations),
    frequency: [FREQUENCY.DAILY, FREQUENCY.WEEKLY, FREQUENCY.MONTHLY],
    featureFlag: 'diagnostics_tab',
  },
};

export const OTHER_SCHEDULED_REPORT_TYPES = {
  trips: {
    key: 'TRIPS',
    label: 'Trips List Export',
    subtypes: [],
    supportsTagsFilter: true,
    supportsAssetDriverFilter: true,
    reportDuration: Object.values(durationForTrips),
    frequency: [FREQUENCY.DAILY, FREQUENCY.WEEKLY, FREQUENCY.MONTHLY],
    additionalParams: ['includeInsignificantTrips', 'unit'],
  },
};

export const COMBINED_SCHEDULED_REPORTS = { ...COACHING_REPORT_TYPES, ...DIAGNOSTICS_REPORT_TYPES, ...OTHER_SCHEDULED_REPORT_TYPES };

export const ASSET_DRIVER_FILTER = [
  {
    label: 'Driver',
    value: 'driverFilter',
  },
  {
    label: 'Asset',
    value: 'assetFilter',
  },
];

export const REQUIRED_FIELDS_FOR_REPORT_TYPES = {
  FLEET_SAFETY: ['duration', 'frequency', 'sendTime'],

  COACHING_SESSION: ['frequency'],

  FLEET_HEALTH_EVENTS: ['duration', 'frequency', 'sendTime'],

  FLEET_CAMERA_EVENTS: ['duration', 'frequency', 'sendTime'],

  FLEET_DEVICE_PING: ['duration', 'frequency', 'sendTime'],

  TRIPS: ['duration', 'frequency', 'sendTime'],

  PRIVACY_MODE: ['duration', 'frequency', 'sendTime'],

  EVENT_LIST: ['duration', 'frequency', 'sendTime'],

  EVENT_COUNT: ['duration', 'frequency', 'sendTime'],

  POTENTIAL_CRASH_EVENTS: ['duration', 'frequency', 'sendTime'],

  FLEET_DVR_REQUESTS: ['duration', 'frequency', 'sendTime'],

  PANIC_EVENTS: ['duration', 'frequency', 'sendTime'],
  CUSTOM_REPORT: ['duration', 'frequency', 'sendTime'],
};

export const REPORT_SECTIONS = [
  {
    sectionTitle: 'Coaching Reports',
    sectionDesc: 'Track coaching activities, outcomes, and opportunities for driver improvement.',
    reports: [
      {
        title: 'Coaching Session Report',
        description: 'This report details coaching sessions conducted in person by a Coach with a specific driver.',
        actions: ['Schedule', 'View'],
        featureFlag: 'reports:coaching_session_report',
      },
      {
        title: 'Coaching Effectiveness (Driver)',
        description:
          'This report analyzes coaching effectiveness, measuring the change in risky behavior before and after coaching sessions.',
        actions: ['View'],
        featureFlag: 'reports:coaching_effectiveness_report',
      },
    ],
  },

  {
    sectionTitle: 'Driver Reports',
    sectionDesc: 'Get a comprehensive view of driver activity and performance.',
    reports: [
      {
        title: 'Privacy Mode Report',
        description: 'This report provides a list of all times privacy mode was enabled and disabled by each driver.',
        actions: ['Schedule', 'Download'],
      },
    ],
  },
  {
    sectionTitle: 'Fleet Reports',
    sectionDesc: 'Understand overall fleet performance through trips, safety, and usage insights.',
    reports: [
      {
        title: 'Fleet Safety Report',
        description:
          'This report summarizes key safety metrics for the fleet, highlighting top drivers, those needing coaching, and the most improved drivers.',
        actions: ['Schedule', 'View'],
      },
      {
        title: 'Video Request List Report',
        description:
          'This report provides a list of all video requests made by the fleet along with the duration and time of the requests.',
        actions: ['Schedule', 'Download'],
        featureFlag: 'reports:video_request_list',
      },
    ],
  },
  {
    sectionTitle: 'Safety Event Reports',
    sectionDesc: 'Analyze event data to identify risks, patterns, and opportunities to improve safety events.',
    reports: [
      {
        title: 'Event Count Report',
        description: 'This report provides a list of event counts by each event category giving fleets insights into safety.',
        actions: ['Schedule', 'Download'],
      },
      {
        title: 'Event List Report',
        description: 'This report provides a list of all safety events giving fleets an overview of violations and safety.',
        actions: ['Schedule', 'Download'],
      },
      {
        title: 'High-G Event List',
        description: 'This report provides a list of all High-G events that have occurred in the fleet.',
        actions: ['Schedule', 'Download'],
        featureFlag: 'reports:high_g_event_report',
      },
      {
        title: 'Panic Button List Report',
        description: 'This report provides the fleet with a list of all panic buttons with an option to filter by driver and assets.',
        actions: ['Schedule', 'Download'],
        featureFlag: 'reports:panic_button_list_report',
      },
    ],
  },
];

export const REPORT_ENTITY_MAP: { [key: string]: string[] } = {
  TRIPS: ['asset', 'driver', 'trip'],
  FLEET_SAFETY: ['asset', 'driver'],
  FLEET_DVR_REQUESTS: ['asset', 'driver'],
  PANIC_EVENTS: ['asset', 'driver'],
  POTENTIAL_CRASH_EVENTS: ['asset', 'driver'],
  PRIVACY_MODE: ['asset', 'driver'],
  EVENT_LIST: ['asset', 'driver', 'event'],
  EVENT_COUNT: ['asset', 'driver', 'event'],
};

export const TALK_TO_DATA_PREDEFINED_QUESTIONS = [
  'Top 5 drivers with the highest number violations?',
  'Count of forward collision warnings for each driver in the fleet?',
  'Can you identify trips where a speed violation and a drowsy event occurred for any driver in a 10-minute window?',
  'Driver with the highest number of cellphone distractions?',
  'Average duration of trips taken by driver in the fleet?',
];

export const TALK_TO_DATA_DATE_RANGES = [
  { label: 'Past 3 days', days: 'Past 3 days' },
  { label: 'Past 7 days', days: 'Past 7 days' },
  { label: 'Past 14 days', days: 'Past 14 days' },
  { label: 'Past 30 days', days: 'Past 30 days' },
];

export const DATE_RANGE_PRESET_TO_DAYS = {
  'Past 3 days': 3,
  'Past 7 days': 7,
  'Past 14 days': 14,
  'Past 30 days': 30,
};
